/**
 * Peer Facade — Strangler Fig Pattern
 *
 * All consumer modules (inputEvents.ts, peerMessage.ts, connectionEvents.ts,
 * player.ts) call these facade functions. The flag USE_NEW_PEER in config.json
 * determines which implementation is used.
 *
 * USE_NEW_PEER=false (default): legacy @playcastdotio/Peer library.
 * USE_NEW_PEER=true:  new ClientPeerManager with @bowbee/peer-lite.
 */

import { createQualityManager } from '@playcastdotio/Peer';
import {
  generators,
  PeerIdType,
  PeerMessage,
  PeerMessageType,
  websocket,
} from '@playcastdotio/Messaging';
import { sendPlayerFrameratePanickedEvent, sendWebRtcStatsGatheredEvent } from '../peerTelemetry/playerConnectionTelemetry';
import { isMobileDevice, isIOS, isSafari, isTouchDevice } from '../../utility/utility';
import * as realtime from './realtimeMessaging';
import { getPersistentPlayerId } from '..';
import { handleHostMessage } from './hostMessaging/handleMessages';
import { invitationState, playerNameState } from '../stores/lobbyStores';
import { authStore, joinState } from '../stores/globalStores';
import { playerConnectionState, mediaStreamState } from '../stores/playerStores';
import config from '../../config.json';
import { ClientPeerManager, type GuestConnectOptions } from './ClientPeerManager';
import { EncodedFrameStatsTap } from './EncodedFrameStatsTap';
import { quickstartRetryPolicy } from './quickstartRetryPolicy';
import { GuestStatsBridge } from './guestStatsBridge';
import {
  PeerLiteConnectionFactory,
  mergeRemoteMediaStreams,
  sampleGuestReclaimConnectionQuestionable,
  isStreamSignalQualityMessage,
  isDisconnectRetryable,
  sampleGuestQuestionableForBuild,
  markGuestConnectionFailed,
  resetGuestConnectionFailed,
  GUEST_TOTAL_RECOVERY_BUDGET_MS,
  GUEST_ICE_REFRESH_TIMEOUT_MS,
  GUEST_STUCK_RECOVERY_RELOAD_MS,
  GUEST_PEER_ID_TIMEOUT_MS,
  toRtcIceServers,
  getGuestDecodeCapability,
  primeGuestDecodeCapability,
  createDocumentVisibilitySource,
  capabilityForEngine,
  evaluateConnectBandwidth,
  type GuestBrowserCapability,
  type GuestBeforeReconnectInfo,
  type InputPhotonSummary,
  type RvfcVideoElement,
} from '@playcastdotio/PeerCore';
import { DeviceMetadataManager } from '../../features/mobile-stream/managers/DeviceMetadataManager';
import { getPreFetchedIceServers, type IceServerCredentials } from './iceServerPrefetch';
import { restoreConnectedAfterIceRecovery, restoreMediaAfterPeerLiteConnected } from './iceRecoveryStatus';
import {
  createPeerLegacy,
  getLegacyPeer,
  closeLegacyPeer,
  sendLegacyPeerMessage,
  handleLegacyPeerMessage,
  devHardResetVideoLegacy,
} from './peerLegacy';

// ========================================
// Shared utilities
// ========================================

/**
 * Reclaim sample. Separate from the initial one because the page-load timings behind the
 * initial verdict are frozen at load: re-running them after a drop describes the link that
 * already failed and reports it healthy.
 */
function sampleReclaimConnectionQuestionable(attempt: number): boolean {
  return sampleGuestReclaimConnectionQuestionable({
      isMobile: isMobileDevice(),
      attempt,
      // A recovery that completes quickly (Android radio sleep) is not evidence of a bad
      // link; one that burned the budget is. The budget is 6s, so "quick" means the whole
      // reclaim ran inside ~3s - see MOBILE_RECLAIM_QUICK_MS.
      recoveredWithinMs: episodeElapsedRecoveryMs(),
    });
}
/**
 * How long the current recovery episode has already run, in ms; undefined when no session
 * is recovering. Drives the quick-recovery exemption in the reclaim hint.
 */
function episodeElapsedRecoveryMs(): number | undefined {
  const remaining = clientPeerManager?.recoveryBudgetRemainingMs();
  if (remaining === undefined) return undefined;
  return Math.max(0, GUEST_TOTAL_RECOVERY_BUDGET_MS - remaining);
}

/**
 * The connection-quality hint the host reads to pick a guest's camera tier, for any
 * (re)build of the peer stack. Delegates to the shared PeerCore rule so the guest and
 * beacon fast-start payloads answer this question identically.
 */
function questionableHintForPeerBuild(): boolean {
  // A live measurement of the link beats the frozen page-load sample: a low-bandwidth
  // connection can be seated on 360p before the stream starts instead of demoted later.
  const connectVerdict = evaluateConnectBandwidth({
    wsHandshakeRttMs: realtime.getWsHandshakeRttMs(),
  });
  if (connectVerdict === 'low-bandwidth') return true;
  return sampleGuestQuestionableForBuild({
    isMobile: isMobileDevice(),
    attempt: clientPeerManager?.getRetryAttempt() ?? 1,
  });
}

const deviceMetadataManager = new DeviceMetadataManager();
function deviceClassificationMetadata() {
  return deviceMetadataManager.classify({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    userAgentDataMobile: (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    touchCapable: isTouchDevice(),
  });
}

/**
 * Runs a probe whose fields are allowed to be absent. A probe describes the link or the
 * platform, never the device classification, so one that throws drops only its own fields and
 * leaves the mandatory classification in the payload.
 */
function optionalProbe<T extends object>(read: () => T): Partial<T> {
  try {
    return read();
  } catch {
    return {};
  }
}

/**
 * What this device can decode in HARDWARE, for the host's send-codec choice.
 *
 * Returned as a spreadable fragment rather than a value because ABSENCE is meaningful: the
 * host reads a missing field as "unknown" and reproduces its pre-existing behaviour, and an
 * empty array as "this guest probed and found none". Publishing `[]` before the probe has
 * answered would tell the host the second thing while meaning the first.
 */
function hardwareDecodeMetadata(): { hardwareDecodeFamilies?: string[] } {
  return optionalProbe(() => {
    const families = getGuestDecodeCapability();
    return families ? { hardwareDecodeFamilies: families } : {};
  });
}

/**
 * Ask the platform what it decodes in hardware, once, at module load.
 *
 * Deliberately NOT awaited anywhere on the connect path: decodingInfo is an async round trip
 * into the platform decoder, and connect is the one sequence where added latency is visible
 * to the player. The connect path reads whatever has landed by then and omits the field if
 * nothing has. Never rejects.
 */
void primeGuestDecodeCapability();

/**
 * The RTCStats capability matrix for the browser this guest is actually running on.
 *
 * Safari-iOS, Android Chrome and desktop Chrome differ in which inbound-rtp stats they
 * expose and in whether the jitter buffer is pinned to 0. The congestion sampler must know
 * which shape to trust; without it, Android's pinned jitter-buffer counters stay flat while
 * the decoder falls behind and the decode-side signal never moves.
 */
export function guestBrowserCapabilityForCurrentDevice(): GuestBrowserCapability | undefined {
  try {
    if (typeof navigator === 'undefined') return undefined;
    const userAgent = navigator.userAgent.toLowerCase();
    if (isIOS() || (isSafari() && isTouchDevice())) return capabilityForEngine('safari-ios');
    if (/android/.test(userAgent)) return capabilityForEngine('android-chrome');
    return capabilityForEngine('desktop-chrome');
  } catch {
    return undefined;
  }
}

export const recordDisconnectReason = (reason: string, context?: string) => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    const key = 'playcast.peer.disconnectHistory';
    const now = new Date().toLocaleString();
    const raw = window.localStorage.getItem(key);
    const history: Array<{ at: string; reason: string; context?: string }> = raw ? JSON.parse(raw) : [];
    history.unshift({ at: now, reason, context });
    window.localStorage.setItem(key, JSON.stringify(history.slice(0, 20)));
  } catch (err) {
    console.warn('[Marketing] Failed to record disconnect reason', err);
  }
};

export const qualityManager = createQualityManager();

// ========================================
// Legacy quality manager wiring (only when USE_NEW_PEER=false)
// ========================================

if (!config.USE_NEW_PEER) {
  const PANIC_COUNT_THRESHOLD = 3;
  const PANIC_HARD_RESET_TIMEOUT = 4000;
  let frameratePanicCount = 0;
  let frameratePanicTimeout: NodeJS.Timeout | undefined;

  qualityManager.onFrameratePanic((reason) => {
    console.error('Framerate Panic!');
    const peer = getLegacyPeer();
    if (peer) {
      let hasTracks = false;
      for (const trackId in peer.incomingTracks.currentState) {
        const track = peer.incomingTracks.currentState[trackId];
        if (track.metadata.type === 'video') { hasTracks = true; break; }
      }
      if (!hasTracks) { frameratePanicCount = 0; return; }
      frameratePanicCount++;
      const hardReset = frameratePanicCount > PANIC_COUNT_THRESHOLD;
      const panicMessage = generators.peer.frameratePanic('player', peer.peerId, hardReset);
      peer.sendMessage(panicMessage);
      sendPlayerFrameratePanickedEvent(hardReset, reason);
      if (frameratePanicTimeout) clearTimeout(frameratePanicTimeout);
      frameratePanicTimeout = setTimeout(() => { frameratePanicCount = 0; }, PANIC_HARD_RESET_TIMEOUT);
    }
  });

  qualityManager.preferredQualityState.addListener((state) => {
    const peer = getLegacyPeer();
    if (peer) {
      console.log('Changing quality to', state.profile);
      peer.changeQualityProfile(state.profile);
    }
  });
}

// ========================================
// NEW PATH — ClientPeerManager
// ========================================

let clientPeerManager: ClientPeerManager | undefined;

/**
 * The remote `<video>`, retained at module scope because the element and the session are
 * created in either order: the player mounts before a session exists on a cold start, and
 * the session outlives the element across a remount. Holding it here lets both sides
 * converge — `setRemoteVideoElement` attaches to a live session, and session creation
 * re-attaches whatever element is already mounted.
 */
let remoteVideoElement: RvfcVideoElement | null = null;

/**
 * Publish the remote `<video>` for latency measurement.
 *
 * This is the only producer of frame-presentation times on the guest. Until it is called
 * the decode-hint collector never registers `requestVideoFrameCallback`, and every
 * calibrated-latency report the host would otherwise receive is simply never sent.
 */
export const setRemoteVideoElement = (element: RvfcVideoElement | null): void => {
  remoteVideoElement = element;
  clientPeerManager?.attachRemoteVideo(element);
};

// Mutable signaling targets -- set before connect(), read by callbacks
let signalingPlayerId = '';
let signalingHostBroadcastId = '';

// ========================================
// Peer Milestone Callbacks (for analytics)
// ========================================

export type PeerMilestoneCallbacks = {
  onPeerIdAcquired?: (peerId: string) => void;
  onSdpOfferSent?: () => void;
  onSdpAnswerReceived?: () => void;
  onIceCandidatesSent?: (candidateCount: number) => void;
  onIceChecking?: () => void;
  onIceConnected?: () => void;
  onRemoteStreamReceived?: () => void;
  onIceFailed?: () => void;
};

let milestoneCallbacks: PeerMilestoneCallbacks = {};
let iceCandidatesSentFired = false;

export function setPeerMilestoneCallbacks(callbacks: PeerMilestoneCallbacks): void {
  milestoneCallbacks = callbacks;
  iceCandidatesSentFired = false;
}


function markTransportDegradedDemo(): void {
  resetIncomingMediaDemo();
  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'reconnecting',
    signalQuality: 'none',
  });
}

/**
 * Reclaim the guest's signaling identity and, critically, collect REFRESHED ICE servers
 * before the reconnect offer goes out.
 *
 * Exported for test: the TURN credential window here is the difference between a cellular
 * failover that survives and one that presents stale relay credentials on a new NAT path.
 */
export async function reclaimPeerIdentityDemo(attempt: number): Promise<RTCIceServer[] | undefined> {
  const peerId = signalingPlayerId || playerConnectionState.currentState.playerPeerId;
  const peerToken = playerConnectionState.currentState.peerToken;
  if (!peerId || !peerToken) return undefined;
  // Gate the reclaim on the realtime WS being re-established on the new path.
  await realtime.realtimeWebsocketReady();
  const iceBeforeJson = JSON.stringify(playerConnectionState.currentState.iceServers ?? null);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      playerConnectionState.removeListener(listener);
      // Do not reconnect under an unanswered reclaim — leave ICE refresh to the next attempt.
      resolve(undefined);
    }, GUEST_ICE_REFRESH_TIMEOUT_MS);
    const listener = playerConnectionState.addListener((state) => {
      if (!state.playerPeerId || !state.peerToken || !state.iceServers) return;
      // Use JSON comparison since structuredClone always creates new references.
      const iceChanged = JSON.stringify(state.iceServers ?? null) !== iceBeforeJson;
      // Success requires evidence the reclaim answer landed: refreshed ICE, rotated token,
      // or a new peer id. Matching the *same* token alone fired on unrelated store ticks
      // and missed token-rotation answers (8s stall).
      const tokenRotated = state.peerToken !== peerToken;
      const peerIdChanged = state.playerPeerId !== peerId;
      if (iceChanged || tokenRotated || peerIdChanged) {
        clearTimeout(timeout);
        playerConnectionState.removeListener(listener);
        signalingPlayerId = state.playerPeerId;
        resolve(toRtcIceServers(state.iceServers));
      }
    });
    realtime.sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType: PeerIdType.PLAYER,
        peerId,
        peerToken,
        peerMetadata: {
          ...deviceClassificationMetadata(),
          displayName: playerNameState.currentState.name,
          ...optionalProbe(() => ({
            connectionQuestionable: sampleReclaimConnectionQuestionable(attempt),
          })),
          ...hardwareDecodeMetadata(),
        },
      }),
    );
  });
}

function resetIncomingMediaDemo(): void {
  mediaStreamState.updateState({
    stream: new MediaStream(),
    audioStream: new MediaStream(),
  });
}

/**
 * One full connection refresh is allowed per join. Reset by createPeer (a genuine new
 * join), spent by refreshConnectionAfterRetriesExhausted.
 */
let connectionRefreshSpent = false;

/**
 * Last resort after PeerCore's reclaim attempts are spent: throw the whole peer stack away
 * and build a new one, the way a fresh join does.
 *
 * A reclaim reuses the live GuestPeerSession — same manager, same cached ICE servers,
 * relay-forced transport — so a session whose state has gone bad reproduces the same
 * failure on every attempt. This path closes the manager, drops the buffered stats and
 * media, and re-runs the connect sequence from scratch, which is materially a different
 * attempt rather than a slower copy of the one that just failed.
 *
 * The signaling peer id is deliberately KEPT: the host holds the match against it for
 * HOST_PLAYER_RECONNECT_HOLD_MS, and arriving under a new id would read as a different
 * player rather than the one coming back.
 *
 * @returns true when a refresh was started, so the caller leaves the UI in its recovering
 * state instead of reporting a terminal disconnect.
 */
function refreshConnectionAfterRetriesExhausted(reason: string): boolean {
  if (connectionRefreshSpent) return false;
  // A host-initiated or otherwise deliberate end is not a failure to recover from.
  if (!isDisconnectRetryable(reason)) return false;
  if (!signalingHostBroadcastId) return false;

  connectionRefreshSpent = true;
  console.log(`[peer] Reclaim attempts spent (${reason}) — refreshing the connection`);
  rebuildGuestPeerStack(reason, { freshEpisode: false });
  return true;
}

/**
 * Throw the peer stack away and build a new one, keeping the signaling identity.
 *
 * Shared by the automatic post-exhaustion refresh and the `rejoinGuestSession` facade, which
 * differ in one important way — see `freshEpisode`.
 *
 * @param freshEpisode Whether the rebuilt session starts a NEW recovery episode.
 *
 *   `false` for the automatic refresh. Two reclaim attempts then a refresh is the whole
 *   budget, not the first half of it: resetting the episode here gave the refreshed session
 *   another two attempts and another ten seconds, so a single bad connection could cost four
 *   attempts and twenty seconds. The refresh IS the escape hatch, so it inherits the running
 *   episode and a refreshed connection that fails immediately goes terminal rather than
 *   starting the ladder over.
 *
 *   `true` for an explicit rejoin — a person tapping a control, or an app-level retry. That
 *   is someone deciding to spend a second budget, not the same failure continuing.
 *
 *   Either way the ATTEMPT counter is reset. A refreshed connection that needs one reclaim
 *   must be able to make it; what bounds it is the episode clock, not a crippled counter.
 *   And once the rebuilt connection holds for GUEST_CONNECTION_STABLE_MS the episode ends
 *   on its own, so a drop minutes later is a new failure with a full budget.
 */
function rebuildGuestPeerStack(reason: string, options: { freshEpisode: boolean }): void {
  // When freshEpisode=false (automatic post-exhaustion refresh), we must NOT call close()
  // because close() calls dispose() which permanently disposes the GuestRecoveryEpisode.
  // The automatic refresh is meant to INHERIT the running episode so a bad link cannot spend
  // four reclaim attempts and ~20s — the ten-second ceiling must bound the whole attempt.
  //
  // When freshEpisode=true (explicit rejoin by the user), we close and start a new episode.
  if (options.freshEpisode) {
    clientPeerManager?.close('player:close:connection_refresh');
    clientPeerManager?.resetRecoveryEpisode();
  }
  // Reset the retry budget so PeerCore's reclaim attempts are available for the refreshed
  // connection. Without this, the reused GuestPeerSession still carries the exhausted
  // retryAttempt counter and a drop inside the stability window skips reclaim entirely.
  clientPeerManager?.resetRetries();
  // The new peer connection starts fresh; the old decode count is meaningless.
  resetDecodedFrameCount();
  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'reconnecting',
    kickReason: reason,
    signalQuality: 'none',
  });
  // A rebuild is unbounded on its own: connect() below does not go through runReconnect, so
  // no attempt watchdog is armed, and createPeerNew may wait on a PEER_GET_ID answer that never
  // arrives. This is the only thing standing between that and a permanent spinner.
  armStuckRecoveryReload(`rebuild: ${reason}`);
  // Pass isRefresh=true when inheriting the episode so connect() knows not to end it.
  createPeerNew(signalingHostBroadcastId, options.freshEpisode ? undefined : { isRefresh: true });
}

/**
 * Single-flight guard for the rejoin facade. Cleared when the rebuilt session reaches a
 * verdict (connected or terminal) or when a genuine new join starts — NOT when the call
 * returns, or two coordinators reacting to the same disconnect would each start a rejoin and
 * leave two peer connections racing for one seat.
 */
let guestRejoinInFlight = false;

/**
 * Frames the decoder has actually produced, from WebRTC stats.
 *
 * The player's stall watchdog cannot rely on requestVideoFrameCallback alone. rVFC reports
 * frame PRESENTATION through the compositor, and Chrome on Android routinely promotes video
 * to a hardware overlay where that path is bypassed — so callbacks go quiet while the picture
 * is playing perfectly. With rVFC as the only signal, a 400ms gap raised a "connection lost"
 * cover on a healthy stream, which is exactly what was reported from the field: the guest
 * announcing a lost connection while the host, whose transport was never disturbed, saw
 * nothing wrong and had no reason to demote.
 *
 * A decode counter is immune to all of that: if it is still climbing, frames are arriving,
 * whatever the compositor is doing with them. Undefined when no session is reporting yet,
 * which callers must read as "unknown", never as "stalled".
 */
let lastDecodedFrameCount: number | undefined;

export function guestDecodedFrameCount(): number | undefined {
  return lastDecodedFrameCount;
}

/**
 * Clear the cached decode count so a new peer connection starts fresh.
 *
 * Without this, `lastDecodedFrameCount` would retain the prior session's cumulative value
 * while `framesDecoded` from WebRTC stats restarts low on the new session. The stall
 * watchdog would then see `decodedNow < lastDecodedCount` and fail to call `markProgress()`,
 * falsely raising the recovering cover.
 */
function resetDecodedFrameCount(): void {
  lastDecodedFrameCount = undefined;
}

/**
 * Last-resort escape from a wedged recovery.
 *
 * Every other bound in the recovery path governs something that reports back. This one covers
 * the paths that do not: `createPeerNew` waits on a PEER_GET_ID answer through a store listener
 * with no timeout, and a rebuilt session calls connect() directly instead of through
 * runReconnect, so it never arms the attempt watchdog. Either can leave the player on
 * "recovering lost connection" forever with nothing left to fire — which is exactly what was
 * reported from the field.
 *
 * It is armed when recovery begins and cleared the moment the session resolves either way, so
 * it can only fire on a session that is genuinely stuck. Reloading is blunt, and deliberately
 * last: the reclaim attempts and the connection refresh both get their turn first.
 */
let stuckRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Module-level timer for the peer ID wait in createPeerNew.
 *
 * Must be tracked here so a second createPeerNew call can cancel any pending timer from a
 * previous invocation. Otherwise the stale timer fires, sees an empty playerPeerId (because
 * the newer attempt reset it), and calls onDisconnected('player:disconnect:signaling_unreachable'),
 * aborting a wait that has not actually timed out.
 */
let peerIdWaitTimer: ReturnType<typeof setTimeout> | null = null;
let peerIdWaitListener: ReturnType<typeof playerConnectionState.addListener> | null = null;

function clearPeerIdWait(): void {
  if (peerIdWaitTimer) {
    clearTimeout(peerIdWaitTimer);
    peerIdWaitTimer = null;
  }
  if (peerIdWaitListener) {
    playerConnectionState.removeListener(peerIdWaitListener);
    peerIdWaitListener = null;
  }
}

/** Test seam. Overridable so the reload can be asserted without navigating the runner. */
let reloadPage: () => void = () => {
  if (typeof window !== 'undefined') window.location.reload();
};

/** Replaces the reload action. Returns the previous one so a test can restore it. */
export function setGuestStuckRecoveryReloadAction(action: () => void): () => void {
  const previous = reloadPage;
  reloadPage = action;
  return previous;
}

function armStuckRecoveryReload(reason: string): void {
  // One episode, one timer: re-arming on every retry would push the ceiling out indefinitely,
  // which is the same stacking bug in a different costume.
  if (stuckRecoveryTimer) return;
  stuckRecoveryTimer = setTimeout(() => {
    stuckRecoveryTimer = null;
    if (playerConnectionState.currentState.status === 'connected') return;
    // Reloading a page whose network is gone does not refresh anything — it throws away a
    // running app that could still recover and replaces it with the browser's offline error
    // page, which needs the person to intervene before anything can happen again. The reload
    // is for a wedged session on a working link; an unreachable network is a different
    // failure and gets reported as one.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.warn(`[peer] Still recovering after ${GUEST_STUCK_RECOVERY_RELOAD_MS}ms (${reason}) — device is offline`);
      // Run the same cleanup onDisconnected would: clear the rejoin guard and the peer id wait
      // so subsequent rejoin attempts are not blocked and orphaned timers do not fire.
      clearGuestRejoinInFlight();
      clearPeerIdWait();
      playerConnectionState.updateState({
        ...playerConnectionState.currentState,
        status: 'disconnected',
        kickReason: 'player:disconnect:device_offline',
        signalQuality: 'none',
      });
      return;
    }
    console.warn(
      `[peer] Still recovering after ${GUEST_STUCK_RECOVERY_RELOAD_MS}ms (${reason}) — reloading`,
    );
    reloadPage();
  }, GUEST_STUCK_RECOVERY_RELOAD_MS);
}

function clearStuckRecoveryReload(): void {
  if (!stuckRecoveryTimer) return;
  clearTimeout(stuckRecoveryTimer);
  stuckRecoveryTimer = null;
}

function clearGuestRejoinInFlight(): void {
  guestRejoinInFlight = false;
}

const peerCallbacks = {
  onConnected: () => {
    // Peer-lite milestone (ICE + data channel). Restore media if ICE recovery cleared it
    // on the same PC before this milestone re-fired.
    clearGuestRejoinInFlight();
    clearStuckRecoveryReload();
    restoreMediaAfterPeerLiteConnected();
    playerConnectionState.updateState({ ...playerConnectionState.currentState, status: 'connected' });
  },
  onDisconnected: (reason: string) => {
    // Flush whatever is buffered so a session shorter than one batch still reports.
    clearGuestRejoinInFlight();
    guestStatsBridge.flush();
    resetDecodedFrameCount();
    recordDisconnectReason(reason, 'marketing-demo:new-peer');
    if (isDisconnectRetryable(reason)) markGuestConnectionFailed();
    resetIncomingMediaDemo();
    // PeerCore has spent its reclaim attempts. Try the connection once more from scratch
    // before telling the app the session is over.
    if (refreshConnectionAfterRetriesExhausted(reason)) return;
    // Nothing is connected to the stream now, so the failure verdict has nothing left to
    // describe. It is deliberately sticky WITHIN a session — that is what keeps a reclaim
    // reporting questionable and holds the camera down while the guest is coming back — but
    // latching it for the page's lifetime meant a mobile guest that hiccupped once was seated
    // on 360p for every later connection and never returned to 480p. The next connection is a
    // different connection and gets judged on its own evidence.
    resetGuestConnectionFailed();
    // A terminal disconnect is an outcome the app can render. The reload exists for the case
    // where nothing renders at all, so it must not fire on top of a CTA or an error card.
    clearStuckRecoveryReload();
    playerConnectionState.updateState({
      ...playerConnectionState.currentState,
      status: 'disconnected',
      kickReason: reason,
      signalQuality: 'none',
    });
  },
  onRemoteStream: (stream: MediaStream) => {
    milestoneCallbacks.onRemoteStreamReceived?.();
    handleRemoteStreamNew(stream);
  },
  onDataMessage: (data: string | ArrayBuffer) => {
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data) as unknown;
        if (isStreamSignalQualityMessage(parsed)) {
          playerConnectionState.updateState({
            ...playerConnectionState.currentState,
            signalQuality: parsed.level,
          });
          return;
        }
        handleHostMessage(parsed as Parameters<typeof handleHostMessage>[0]);
      } catch {
        console.warn('[ClientPeerManager] Invalid JSON data channel message');
      }
    }
  },
  onSignalGenerated: (sdp: RTCSessionDescriptionInit) => {
    if (!sdp.sdp) {
      throw new Error('SDP is undefined');
    }
    if (sdp.type === 'offer') {
      milestoneCallbacks.onSdpOfferSent?.();
    }
    const msgType = sdp.type === 'offer' ? PeerMessageType.PEER_SDP_OFFER : PeerMessageType.PEER_SDP_ANSWER;
    realtime.sendMessage(websocket.generators.peer(msgType, { fromId: signalingPlayerId, toId: signalingHostBroadcastId, sdp: { type: sdp.type as 'offer' | 'answer', sdp: sdp.sdp ?? '' } }));
  },
  onIceCandidatesGenerated: (candidates: RTCIceCandidate[]) => {
    if (!iceCandidatesSentFired) {
      iceCandidatesSentFired = true;
      milestoneCallbacks.onIceCandidatesSent?.(candidates.length);
    }
    const json = candidates.map((c) => JSON.stringify(c));
    realtime.sendMessage(websocket.generators.peer(PeerMessageType.PEER_ICE_CANDIDATE, { fromId: signalingPlayerId, toId: signalingHostBroadcastId, candidates: { json } }));
  },
  onRetrying: (attempt: number, reason: string) => {
    armStuckRecoveryReload(`retrying: ${reason}`);
    console.log(`[ClientPeerManager] Retrying (attempt ${attempt}): ${reason}`);
    // Whatever happens next is a recovery, so any rebuild must be hinted as one.
    markGuestConnectionFailed();
    resetDecodedFrameCount();
    resetIncomingMediaDemo();
    playerConnectionState.updateState({
      ...playerConnectionState.currentState,
      status: 'reconnecting',
      kickReason: reason,
      signalQuality: 'none',
    });
  },
  onIceDisconnected: () => {
    markTransportDegradedDemo();
  },
  onTransportLost: () => {
    markTransportDegradedDemo();
  },
  onBeforeReconnect: async (info: GuestBeforeReconnectInfo) => {
    const iceServers = await reclaimPeerIdentityDemo(info.attempt);
    return iceServers ? { iceServers } : {};
  },
  onIceChecking: () => {
    milestoneCallbacks.onIceChecking?.();
  },
  onIceConnected: () => {
    restoreConnectedAfterIceRecovery();
    milestoneCallbacks.onIceConnected?.();
  },
  onIceFailed: () => {
    milestoneCallbacks.onIceFailed?.();
  },
};

/**
 * Client telemetry + quality-mode feedback for the USE_NEW_PEER path.
 *
 * Both only ever existed in peerLegacy.ts, behind the disabled flag, so the shipping stack
 * reported nothing. Fed from the PeerCore sampler that already runs at 1Hz inside the guest
 * session — deliberately NOT a second getStats() poll, which would double the cost of the
 * most expensive observation a mobile client makes.
 */
const guestStatsBridge = new GuestStatsBridge({
  sendBatch: (stats, interval) => realtime.sendMetricPlayerBatchWebrtcStats(stats, interval),
  sendAnalytics: (stats, interval) => sendWebRtcStatsGatheredEvent(stats, interval),
  sendToHost: (hardReset) => {
    const peerId = playerConnectionState.currentState.playerPeerId;
    if (!peerId) return;
    clientPeerManager?.send(
      JSON.stringify(generators.peer.frameratePanic('player', peerId, hardReset)),
    );
  },
  sendPanicEvent: (hardReset, reason) => sendPlayerFrameratePanickedEvent(hardReset, reason),
});

function ensureClientPeerManager(): ClientPeerManager {
  if (!clientPeerManager) {
    clientPeerManager = new ClientPeerManager(
      new PeerLiteConnectionFactory(),
      peerCallbacks,
      quickstartRetryPolicy,
      // The production wiring for the backgrounding fix. Without a source here the recovery
      // clock never stops, and a mobile guest that switches apps mid-session is still
      // guaranteed a terminal disconnect no matter what PeerCore can do.
      createDocumentVisibilitySource(),
      {
        isMobile: isMobileDevice(),
        capability: guestBrowserCapabilityForCurrentDevice(),
        // Receive-side encoded-frame sizes for the jitter-buffer diagnosis; absent on engines
        // without RTCRtpScriptTransform, and the wire field with it.
        ...(EncodedFrameStatsTap.isSupported() ? { encodedFrameTap: new EncodedFrameStatsTap() } : {}),
      },
    );
    clientPeerManager.onStatsSample((sample) => {
      // Authoritative frame progress, recorded alongside telemetry rather than through a
      // second listener (onStatsSample holds one). The player's stall watchdog needs a signal
      // that survives Android's compositor quirks — see guestDecodedFrameCount below.
      lastDecodedFrameCount = sample.framesDecoded;
      guestStatsBridge.onSample(sample);
    });
    // The player usually mounts before the first session exists, so the element that was
    // published then would otherwise be dropped on the floor.
    clientPeerManager.attachRemoteVideo(remoteVideoElement);
  }
  return clientPeerManager;
}

/**
 * Pre-warm the RTCPeerConnection with TURN credentials so ICE candidate
 * gathering starts immediately, well before a match is found.
 *
 * Called automatically by iceServerPrefetch.ts the instant credentials arrive.
 * Accepts optional pre-resolved credentials to avoid re-awaiting the promise.
 */
export async function preWarmPeerConnection(credentials?: IceServerCredentials | null): Promise<void> {
  if (!config.USE_NEW_PEER) return;

  const iceServers = credentials ?? await getPreFetchedIceServers();
  if (!iceServers) {
    console.log('[peer] No prefetched ICE servers available, skipping pre-warm');
    return;
  }

  const manager = ensureClientPeerManager();
  if (manager.hasPreWarmedConnection()) return;

  manager.preWarm(toRtcIceServers(iceServers) ?? []);
  console.log('[peer] Pre-warmed RTCPeerConnection with prefetched TURN credentials');
}

async function connectClientPeerManager(
  playerId: string,
  hostBroadcastId: string,
  connectOptions?: GuestConnectOptions,
): Promise<void> {
  signalingPlayerId = playerId;
  signalingHostBroadcastId = hostBroadcastId;

  const manager = ensureClientPeerManager();
  const stateIceServers = playerConnectionState.currentState.iceServers;
  const prefetchedIceServers = !stateIceServers ? await getPreFetchedIceServers() : null;
  const iceServers = stateIceServers ?? prefetchedIceServers;

  const metadata = {
    displayName: playerNameState.currentState.name,
    playerPersistentId: getPersistentPlayerId(),
    email: authStore.currentState.email,
    playerAuthId: authStore.currentState.playerAuthId,
    invitationPlayerId: invitationState.currentState.invitationPlayerId || authStore.currentState.playerAuthId || getPersistentPlayerId(),
    ...deviceClassificationMetadata(),
  };

  if (!iceServers) {
    console.warn('[peer] No ICE servers available from state or prefetch — TURN relay will be unavailable');
  }

  manager.connect(hostBroadcastId, toRtcIceServers(iceServers) ?? [], metadata, connectOptions);
}

function createPeerNew(hostBroadcastId: string, connectOptions?: GuestConnectOptions): void {
  if (clientPeerManager?.isConnected()) {
    clientPeerManager.close('player:close:duplicate_peer');
  }

  // Cancel any pending peer ID wait from a previous invocation. Without this, a stale timer
  // can fire after a rebuild has already started a new wait, tearing down the session that
  // has not actually timed out.
  clearPeerIdWait();

  // A fresh join must not inherit the previous session's samples or panic streak.
  guestStatsBridge.reset();

  // Clear stale media from any prior session so mergeRemoteMediaStreams
  // starts fresh and doesn't retain dead audio/video tracks.
  mediaStreamState.updateState({
    stream: new MediaStream(),
    audioStream: new MediaStream(),
  });

  // If fast-start already pre-created a peer ID, skip the PEER_GET_ID round-trip
  const existingPeerId = playerConnectionState.currentState.playerPeerId;
  if (existingPeerId) {
    console.log('[peer] Using pre-created peer ID from fast-start:', existingPeerId);
    milestoneCallbacks.onPeerIdAcquired?.(existingPeerId);
    playerConnectionState.updateState({
      ...playerConnectionState.currentState,
      status: 'connecting',
    });
    connectClientPeerManager(existingPeerId, hostBroadcastId, connectOptions);
    return;
  }

  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'connecting',
    playerPeerId: '',
  });

  realtime.sendMessage(
    websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
      peerIdType: PeerIdType.PLAYER,
      peerMetadata: {
        ...deviceClassificationMetadata(),
        displayName: playerNameState.currentState.name,
        ...optionalProbe(() => ({ connectionQuestionable: questionableHintForPeerBuild() })),
      },
    }),
  );

  // The answer arrives over the realtime websocket. When the network is gone the request goes
  // nowhere and this listener would wait forever, parking the session before a peer connection
  // exists at all — nothing downstream can rescue that, because nothing downstream has started.
  peerIdWaitListener = playerConnectionState.addListener((state) => {
    if (state.playerPeerId === '') return;
    clearPeerIdWait();
    milestoneCallbacks.onPeerIdAcquired?.(state.playerPeerId);
    connectClientPeerManager(state.playerPeerId, hostBroadcastId, connectOptions);
  });

  // Start the clock only once the socket can actually carry the request.
  //
  // sendMessage QUEUES when the websocket is not open yet and flushes on connect, so the
  // request is never lost — but the time spent waiting for the socket was counting against this
  // timeout. A cold start or a websocket reconnect could therefore burn the whole budget before
  // the server had been asked anything, and the guest reported an unreachable-signaling failure
  // on a perfectly healthy connection. The bound is meant to catch a server that never answers,
  // not a socket that took a moment to open.
  const listenerAtArm = peerIdWaitListener;
  void realtime.realtimeWebsocketReady().then(() => {
    // A newer join (or the answer itself) already tore this wait down.
    if (peerIdWaitListener !== listenerAtArm || peerIdWaitListener === null) return;
    peerIdWaitTimer = setTimeout(() => {
      clearPeerIdWait();
      if (playerConnectionState.currentState.playerPeerId !== '') return;
      console.warn(`[peer] No peer id after ${GUEST_PEER_ID_TIMEOUT_MS}ms — signaling is unreachable`);
      // Report it the same way any other unrecoverable failure is reported, so the app renders
      // an actionable state instead of a spinner that nothing will ever clear.
      peerCallbacks.onDisconnected('player:disconnect:signaling_unreachable');
    }, GUEST_PEER_ID_TIMEOUT_MS);
  }).catch(() => {
    // The realtime socket never authenticated within its timeout. A newer join (or the answer
    // itself) may have already torn this wait down; if not, report signaling unreachable so
    // the app does not sit on a permanent spinner.
    if (peerIdWaitListener !== listenerAtArm || peerIdWaitListener === null) return;
    clearPeerIdWait();
    if (playerConnectionState.currentState.playerPeerId !== '') return;
    console.warn('[peer] Realtime websocket auth timed out — signaling is unreachable');
    peerCallbacks.onDisconnected('player:disconnect:signaling_unreachable');
  });
}

function handleRemoteStreamNew(stream: MediaStream): void {
  // peer-lite emits streamRemote per ontrack; preserve audio/video across
  // separate events (same race adminSessionPeer already guards against).
  const merged = mergeRemoteMediaStreams(stream, mediaStreamState.currentState);

  merged.stream.getVideoTracks().forEach((t) => monitorTrack(t, 'video'));
  merged.audioStream.getAudioTracks().forEach((t) => monitorTrack(t, 'audio'));

  stream.onremovetrack = (ev) => {
    console.error('[peer] 🚨 Remote stream track removed:', {
      kind: ev.track.kind,
      id: ev.track.id,
      readyState: ev.track.readyState,
      timestamp: new Date().toISOString(),
    });
  };

  mediaStreamState.updateState(merged);
}

function monitorTrack(track: MediaStreamTrack, kind: 'video' | 'audio'): void {
  track.onended = () => {
    console.error(`[peer] 🚨 Remote ${kind} track ENDED`, {
      id: track.id,
      label: track.label,
      readyState: track.readyState,
      muted: track.muted,
      timestamp: new Date().toISOString(),
    });
    if (kind === 'video') {
      window.dispatchEvent(new CustomEvent('playcast:video:track-ended', { detail: { trackId: track.id } }));
    }
  };

  track.onmute = () => {
    console.warn(`[peer] ⚠️ Remote ${kind} track MUTED`, {
      id: track.id,
      readyState: track.readyState,
      timestamp: new Date().toISOString(),
    });
  };

  track.onunmute = () => {
    console.log(`[peer] ✅ Remote ${kind} track UNMUTED`, {
      id: track.id,
      readyState: track.readyState,
      timestamp: new Date().toISOString(),
    });
  };
}

function applyRemoteSdp(manager: ClientPeerManager, sdp: RTCSessionDescriptionInit): void {
  manager.handleRemoteSdp(sdp).catch((err) => {
    console.warn('[peer] Failed to apply remote SDP:', err);
  });
}

function handleIncomingPeerMessageNew(message: PeerMessage): void {
  if (!clientPeerManager) return;
  // Drop signaling from anyone but the host this session dialed. The realtime server
  // validates ownership/pairing, but a legitimate peer that knows this guest's peerId
  // could still inject SDP/ICE with its own honest fromId.
  const fromId = 'fromId' in message ? (message.fromId as string | undefined) : undefined;
  if (signalingHostBroadcastId && fromId && fromId !== signalingHostBroadcastId) {
    console.warn('[peer] Ignoring signaling from unexpected peer:', fromId);
    return;
  }
  switch (message.type) {
    case PeerMessageType.PEER_SDP_OFFER:
      if ('sdp' in message && message.sdp) {
        applyRemoteSdp(clientPeerManager, message.sdp as RTCSessionDescriptionInit);
      }
      break;
    case PeerMessageType.PEER_SDP_ANSWER:
      if ('sdp' in message && message.sdp) {
        milestoneCallbacks.onSdpAnswerReceived?.();
        applyRemoteSdp(clientPeerManager, message.sdp as RTCSessionDescriptionInit);
      }
      break;
    case PeerMessageType.PEER_ICE_CANDIDATE:
      if ('candidates' in message && message.candidates) {
        const jsonCandidates = (message.candidates as { json: string[] }).json;
        for (const candidateJson of jsonCandidates) {
          try {
            const candidate = new RTCIceCandidate(JSON.parse(candidateJson));
            clientPeerManager.handleRemoteIceCandidate(candidate).catch((err) => {
              console.warn('[peer] Failed to add remote ICE candidate:', err);
            });
          } catch (e) {
            console.warn('[peer] Failed to parse ICE candidate:', e);
          }
        }
      }
      break;
  }
}

// ========================================
// FACADE — unified API for consumers
// ========================================

export const createPeer = async (hostBroadcastId: string) => {
  if (!hostBroadcastId) { console.error('No host broadcast id'); return; }
  // A join initiated from the app gets its own refresh budget.
  connectionRefreshSpent = false;
  clearGuestRejoinInFlight();
  clearStuckRecoveryReload();
  if (config.USE_NEW_PEER) { createPeerNew(hostBroadcastId); return; }
  return createPeerLegacy(hostBroadcastId);
};

/**
 * Tear the peer stack down and re-establish it from scratch, as a fresh join does, keeping
 * the signaling identity so the host still recognises the returning player.
 *
 * This is the single place every exhausted recovery path ends up, so the beacon and
 * quickstart coordinators stop each inventing their own rebuild — and their own budget on
 * top of PeerCore's, which is how an unrecoverable transport came to cost ~33 seconds.
 *
 * A rejoin ENDS the current recovery episode and starts a new one: the rebuilt session gets
 * a full budget rather than inheriting a spent one.
 *
 * Never throws — callers are error handlers, and an error handler that throws leaves the UI
 * exactly where the failure did.
 *
 * @returns true when a rejoin was started; false when one is already in flight, when
 * PeerCore is still recovering on its own, when the end was deliberate, or when there is no
 * broadcast to rejoin.
 */
export async function rejoinGuestSession(reason: string): Promise<boolean> {
  try {
    if (!config.USE_NEW_PEER) return false;
    // Deliberate ends (host closed, kicked, ad mode, user exit) are not failures to recover
    // from; isDisconnectRetryable is false for all of them.
    if (!isDisconnectRetryable(reason)) return false;
    if (!signalingHostBroadcastId) return false;
    if (guestRejoinInFlight) return false;
    // PeerCore owns the recovery while its own reclaim is still running; rebuilding now
    // would race a reclaim that is about to land and produce two peer connections.
    if (clientPeerManager?.isReconnectInProgress()) return false;

    guestRejoinInFlight = true;
    // A rejoin is contractually a fresh start, so the rebuild gets its own refresh budget.
    connectionRefreshSpent = false;
    console.log(`[peer] Rejoining the guest session (${reason})`);
    rebuildGuestPeerStack(reason, { freshEpisode: true });
    return true;
  } catch (error) {
    guestRejoinInFlight = false;
    console.warn('[peer] Rejoin failed to start', error);
    return false;
  }
}

export const sendPeerMessage = (message: any): void => {
  if (config.USE_NEW_PEER) { clientPeerManager?.send(JSON.stringify(message)); return; }
  sendLegacyPeerMessage(message);
};

export const closePeer = (reason: string): void => {
  // The session is over and nothing is connected: the next join is judged on its own link,
  // not on this one's history. See the note in onDisconnected.
  clearStuckRecoveryReload();
  resetGuestConnectionFailed();
  resetDecodedFrameCount();
  if (config.USE_NEW_PEER) { clientPeerManager?.close(reason); return; }
  closeLegacyPeer(reason);
};

export const handleIncomingPeerMessage = (message: PeerMessage): void => {
  if (config.USE_NEW_PEER) { handleIncomingPeerMessageNew(message); return; }
  handleLegacyPeerMessage(message);
};

export const getPlayerPeerId = (): string => {
  return playerConnectionState.currentState.playerPeerId || '';
};

export const playerDisconnect = () => {
  closePeer('player:close:manual_disconnect');
  joinState.updateState({ ...joinState.currentState, state: 'playerDisconnected' });
};

export const getPeer = () => getLegacyPeer();

export const devHardResetVideo = () => {
  if (!config.USE_NEW_PEER) devHardResetVideoLegacy();
};

export const getClientPeerManager = (): ClientPeerManager | undefined => clientPeerManager;

/**
 * Claim a sequence number for an input about to be sent, or null to send it untagged.
 *
 * Null is the normal answer — only a sampled fraction of inputs is measured, and there is
 * nothing to do when no session exists yet.
 */
export const sampleInputForPhoton = (): number | null =>
  clientPeerManager?.sampleInputForPhoton() ?? null;

/** Rolling input-to-photon latency for telemetry and the debug overlay. */
export const guestInputPhotonSummary = (): InputPhotonSummary | undefined =>
  clientPeerManager?.inputPhotonSummary();

/** True while the guest PeerCore session is still retrying / ICE-recovering. */
export const isGuestReconnectInProgress = (): boolean =>
  clientPeerManager?.isReconnectInProgress() ?? false;

/**
 * Milliseconds left in the current recovery episode, or `undefined` when there is no guest
 * session to ask.
 *
 * App-level retries consult this before starting another attempt: every layer honouring the
 * same clock is what keeps the total bounded, and bounding each layer separately is exactly
 * the bug. `undefined` means "no session", not "no budget" — callers must not read it as a
 * reason to give up.
 */
export const guestRecoveryBudgetRemainingMs = (): number | undefined =>
  clientPeerManager?.recoveryBudgetRemainingMs();

/**
 * Ask the host for a keyframe on the live guest session.
 *
 * Used by the stall watchdog: a frozen picture over a healthy transport is usually decode
 * desync, which a fresh IDR resolves. The host rate-limits client-originated requests, so a
 * refused one is harmless.
 */
export const requestGuestKeyframe = (): boolean => clientPeerManager?.requestKeyframe() ?? false;

/** Session-owned evidence and action arbitration; reading never registers another rVFC producer. */
export const guestPresentationEvidence = () => clientPeerManager?.getPresentationEvidence();
export const guestRecoveryActionArbiter = () => clientPeerManager?.getRecoveryActionArbiter();

